import { QueryExpr } from '@apis/Resources';
import { ActionIcon, Group } from '@mantine/core';
import { InfoCircle, Plus } from 'tabler-icons-react';
import { ComponentPropsWithoutRef, ComponentPropsWithRef, useCallback, useEffect, useState } from 'react';
import { TooltipWhite } from '@root/Design/Primitives';
import {
    SettingsSection,
    SettingsSectionItem,
    SettingsSectionItemBody,
    SettingsSectionItemHeader,
    SettingsSectionItemHeaderLabel,
} from '@root/Design/Settings';
import { DatasourceSchemaContext, withResolvedSchemaCtx } from '../Filter/DatasourceContext';
import { DataFilterModel, DataFilters } from '../Filter/Filters';
import { FieldPicker } from '../Picker/FieldPicker';
import { LineItemCompactToken } from '../Filter/Design';
import { IBaseInputSpec, ICustomInputSpec } from './InputSpec';
import { SettingsLabeled } from './SettingsInputs';
import { useEvent } from '@root/Services/EventEmitter';

interface IBaseEditorFilterProps {
    value: QueryExpr[];
    onChange: (filters: QueryExpr[]) => void;
    onStateChange?: (isValid?: boolean, isEmpty?: boolean) => void;
    filterProps?: Partial<ComponentPropsWithRef<typeof DataFilters>>;
    schemaCtx: DatasourceSchemaContext;
    labels?: { comparitorNoun?: string };
}
export const FilterSettings = withResolvedSchemaCtx(function BaseEditorFilter(props: IBaseEditorFilterProps) {
    const { filterProps, labels, onChange, onStateChange, schemaCtx, value } = props;
    const { schemaSvc, queryDescriptorSvc, valueProvider } = schemaCtx;
    const { fieldInfoProvider } = queryDescriptorSvc;
    const [filterModel, setFilterModel] = useState<DataFilterModel>();
    const fieldPickerRenderer = useCallback((select: (field: string) => void) => {
        return <FieldPicker mode="single" selections={[]} schema={schemaSvc} onChange={(fields) => select(fields[0].path)} />;
    }, []);
    const handleAddFilter = useCallback(() => filterModel?.addEmptyFilter(true, true), [filterModel]);
    const handleStateChange = useCallback(() => {
        if (filterModel) {
            value.splice(0, Infinity, ...filterModel.getFilters());
            onChange(value);
            onStateChange?.(!!filterModel.getValidationIssues().length, !filterModel?.filters.length);
        }
    }, [filterModel, onChange, onStateChange]);
    useEvent(filterModel?.filtersChanged, handleStateChange);

    return (
        <>
            <DataFilters
                dataFiltersAsLineItem
                lineItemCompact
                onModelLoaded={setFilterModel}
                fieldInfoProvider={fieldInfoProvider}
                align="end"
                renderFieldPicker={fieldPickerRenderer}
                filters={value}
                showErrors
                onChange={() => {}}
                valueProvider={valueProvider}
                {...filterProps}
            />
            <LineItemCompactToken onClick={handleAddFilter}>
                <Group spacing={4}>
                    <Plus size={16} /> Add {filterModel?.filters.length ? 'another' : 'a'} {labels?.comparitorNoun ?? 'filter'}
                </Group>
            </LineItemCompactToken>
        </>
    );
});

interface IFilterSettingsSectionItemProps extends IBaseEditorFilterProps {
    sectionItemTitle?: string;
    helpText?: string;
}
export function FilterSettingsSectionItem(props: IFilterSettingsSectionItemProps) {
    const { helpText, sectionItemTitle, ...rest } = props;

    return (
        <SettingsSectionItem>
            {!sectionItemTitle && !helpText ? null : (
                <SettingsSectionItemHeader>
                    <SettingsSectionItemHeaderLabel>{sectionItemTitle ?? 'Filters'}</SettingsSectionItemHeaderLabel>
                    {!helpText ? null : (
                        <TooltipWhite width={300} multiline label={helpText}>
                            <ActionIcon>
                                <InfoCircle size={16} />
                            </ActionIcon>
                        </TooltipWhite>
                    )}
                </SettingsSectionItemHeader>
            )}
            <SettingsSectionItemBody>
                <FilterSettings {...rest} />
            </SettingsSectionItemBody>
        </SettingsSectionItem>
    );
}

interface IFilterSettingsSectionProps extends IFilterSettingsSectionItemProps {
    sectionTitle?: string;
}
export function FilterSettingsSection(props: IFilterSettingsSectionProps) {
    const { sectionTitle, ...rest } = props;

    return (
        <SettingsSection title={sectionTitle ?? 'Filters'}>
            <FilterSettingsSectionItem {...rest} />
        </SettingsSection>
    );
}

type InlineFilterSettingsProps = IBaseEditorFilterProps & { spec: IBaseInputSpec<{ fullWidth?: boolean }> };
function InlineFilterSettings(props: IBaseEditorFilterProps & { spec: IBaseInputSpec<{ fullWidth?: boolean }> }) {
    const { spec, ...rest } = props;
    return (
        <SettingsLabeled spec={spec}>
            <FilterSettings {...rest} />
        </SettingsLabeled>
    );
}

export function createFilterSettingsInput<T, K extends keyof T>(
    model: T,
    filterProp: K & (T[K] extends QueryExpr[] ? K : never),
    schemaCtx: DatasourceSchemaContext,
    label?: string,
    icon?: React.ReactNode,
    labels?: IBaseEditorFilterProps['labels'],
    filterProps?: Partial<ComponentPropsWithRef<typeof DataFilters>>
) {
    function UniqueFilterSettings(props: InlineFilterSettingsProps) {
        return <InlineFilterSettings {...props} />;
    }
    return {
        type: 'custom',

        onChange: (filters) => {
            (model as any)[filterProp] = filters;
        },
        component: UniqueFilterSettings,
        get value() {
            return ((model as any)[filterProp] ??= []) as QueryExpr[];
        },
        props: {
            schemaCtx,
            filterProps,
            labels,
            spec: { label, icon, presentationOptions: { fullWidth: true } },
        },
    } as ICustomInputSpec<QueryExpr[], ComponentPropsWithoutRef<typeof InlineFilterSettings>>;
}
